import { nothing } from 'lit-html';
import { html, property } from 'lit-element';
import {
  KatLitElement,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import { KatSelectionCard } from '../selection-card/selection-card';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './selection-card-group.lit.scss';

/**
 * @component {kat-selection-card-group} KatSelectionCardGroup A selection card group collects multiple selection cards under a single element to enable single- and multi-select across a set of cards.
 * @guideline Do Ensure kat-selection-cards have "radio" selection-type.
 * @status Production
 * @theme flo
 * @example Basic {"multiple": "true", "options": [{"header": "Please select this kat", "subheader": "This is a very good kat.", "body": "His name is Felix and he likes belly rubs", "footer": "He would be really disappointed if you didn't select him.", "value": "felix", "imageSrc": "https://m.media-amazon.com/images/G/01/katal/internal/SquareCat1.jpg"}, {"header": "Don't select this kat", "subheader": "This is not a good kat.", "body": "His name is Kyle and he scratches", "footer": "He would still be disappointed if you didn't select him though.", "value": "kyle", "imageSrc": "https://m.media-amazon.com/images/G/01/katal/internal/example_cat1._CB467620335_.png", "disabled": "true"}]}
 * @example SlottedWithKatGrid {"multiple": "false", "content": "
 *   <style>
 *     kat-selection-card {
 *       margin-bottom: 16px;
 *     }
 *   </style>
 *   <div class=\"kat-row\">
 *     <kat-selection-card class=\"kat-col-md-6\" value=\"1\">
 *       <span>Option 1</span>
 *     </kat-selection-card>
 *     <kat-selection-card class=\"kat-col-md-6\" value=\"2\">
 *       <span>Option 2</span>
 *     </kat-selection-card>
 *     <kat-selection-card class=\"kat-col-md-6\" value=\"3\">
 *       <span>Option 3</span>
 *     </kat-selection-card>
 *     <kat-selection-card class=\"kat-col-md-6\" value=\"4\">
 *       <span>Option 4</span>
 *     </kat-selection-card>
 *   </div>
 * "}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-selection-card-group')
export class KatSelectionCardGroup extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * The value or values of the currently selected Selection Card(s).
   */
  @property({ reflect: true })
  value?: string[];

  /** Controls whether or not multiple cards can be selected. */
  @property()
  multiple?: boolean;

  /** An array of options used to populate the group with Selection Cards. Slotted Selection Cards will take precedence. */
  @property()
  options?: {
    header: string;
    subheader?: string;
    body?: string;
    footer?: string;
    value: string;
    imageSrc?: string;
    imageAlt?: string;
    disabled?: boolean;
    checked?: boolean;
  }[];

  /** Emits when a child Selection Card is selected or unselected. */
  @event('change', true)
  private _change: EventEmitter<{ value: string[] }>;

  updated(changedProperties) {
    if (changedProperties.has('value')) {
      this.setCheckedBasedOnValue();
    }
  }

  private setCheckedBasedOnValue() {
    if (this.multiple) {
      this.getChildren().forEach(child => {
        child.checked = !!child.value && this.value?.includes(child.value);
      });
    } else {
      if (this.value?.length) {
        const firstMatchingChild = Array.from(this.getChildren()).find(
          child => this.value[0] === child.value
        );
        if (firstMatchingChild) {
          firstMatchingChild.checked = true;
        }
      } else {
        this.getChildren().forEach(child => {
          child.checked = false;
        });
      }
    }
  }

  private getSlottedChildren() {
    return this.querySelectorAll<KatSelectionCard>('kat-selection-card');
  }

  private getShadowedChildren() {
    return this.shadowRoot.querySelectorAll<KatSelectionCard>(
      'kat-selection-card'
    );
  }

  private getChildren() {
    if (checkSlots(this).default) {
      return this.getSlottedChildren();
    }

    return this.getShadowedChildren();
  }

  private renderSelectionCards() {
    if (checkSlots(this).default) {
      return nothing;
    }

    const cards = this.options?.map(
      option =>
        html`<kat-selection-card
          header=${option.header}
          value=${option.value}
          .subheader=${option.subheader}
          .body=${option.body}
          .footer=${option.footer}
          .imageSrc=${option.imageSrc}
          .imageAlt=${option.imageAlt}
          ?multiple=${!!this.multiple}
          ?disabled=${option.disabled}
          ?checked=${option.checked}
        ></kat-selection-card>`
    );

    return cards;
  }

  private handleChange(evt: MouseEvent | KeyboardEvent) {
    evt.stopImmediatePropagation();
    const clickedCard = evt.target as KatSelectionCard;

    if (this.multiple) {
      const selected = [];
      this.getChildren().forEach(child => {
        if (child.checked) {
          selected.push(child.value);
        }
      });

      this.value = selected;
    } else {
      this.getChildren().forEach(child => {
        if (child !== clickedCard) {
          child.checked = false;
        } else {
          child.checked = true;
        }
      });
      this.value = [clickedCard?.value];
    }

    this._change.emit({ value: this.value });
  }

  render() {
    this.getSlottedChildren().forEach(
      child => (child.multiple = !!this.multiple)
    );
    const renderedCards = this.renderSelectionCards();

    return html`
      <div @change=${this.handleChange}>
        <slot role="selectiongroup">${renderedCards}</slot>
      </div>
    `;
  }
}
